import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, Target, Zap, Users, Activity, DollarSign, CheckCircle2, AlertTriangle, Info, ChevronDown, ChevronUp, BookOpen, GitCompare } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import RelatedReferences from '../components/reference/RelatedReferences';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const ExpandableSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={cn("h-5 w-5", iconColor)} />}
          <span className="text-base font-semibold text-slate-900">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
};

export default function ROEReferencePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Smooth-scroll to the anchor target when the page is opened with
  // a hash (e.g. /roe-reference#roe-formula). Runs once on mount and
  // whenever the hash changes thereafter.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, '');
    // Defer to the next frame so the target element is mounted.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-teal-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-teal-400', 'ring-offset-2'), 1800);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="ROE Reference"
        description="Understanding Return on Expectation — what it measures, how it's calculated, and how it differs from ROI."
        theme={__pageTheme}
        icon={Target}
        testId="roe-reference-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="roe-reference-page">
      {/* Header */}
      <div className="flex items-start justify-between">
                <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>

      {/* The Problem ROE Solves */}
      <Card id="problem" className="border-2 border-teal-100 bg-gradient-to-br from-teal-50/50 to-white scroll-mt-24 transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-teal-600" />
            The Problem ROE Solves
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>
            Most L&D models assume that if you build capability, performance will follow. <strong>This assumption is wrong.</strong> Research consistently shows that only 10-20% of training translates into sustained behavior change on the job. The rest is lost to:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">Lack of Manager Support</p>
              <p className="text-[10px] text-rose-600 mt-0.5">Managers don't reinforce new skills — learners receive no encouragement to change behavior.</p>
            </div>
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">Environmental Barriers</p>
              <p className="text-[10px] text-rose-600 mt-0.5">Workplace systems, processes, or culture prevent application of new capabilities.</p>
            </div>
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">Confidence Gap</p>
              <p className="text-[10px] text-rose-600 mt-0.5">Learners know what to do but lack confidence to change established patterns of behavior.</p>
            </div>
          </div>
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <p className="text-sm text-teal-800">
              <strong>ROE quantifies this gap.</strong> It measures the percentage of predicted workforce capacity (PE Ideal) that actually materializes as real-world behavior change (PE Validated). This makes the invisible visible — turning "we think training worked" into "we can prove how much transferred."
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Where ROE Sits in the ROI Waterfall */}
      <Card
        id="waterfall"
        className="scroll-mt-24 transition-shadow border-2 border-teal-100 bg-gradient-to-br from-teal-50/40 via-white to-indigo-50/30 shadow-sm"
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-teal-600" />
              Where ROE Sits in the Learning ROI Waterfall
            </CardTitle>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-sm">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2L3 7l7 5 7-5-7-5zM3 13l7 5 7-5M3 10l7 5 7-5"/></svg>
              The Validation Gate
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-2">
          <p className="text-sm text-slate-600">
            The ROI Waterfall is the complete causal chain from learning improvement to financial return. ROE sits at the critical junction between <strong>predicted capacity</strong> and <strong>proven performance</strong> — it's the gate that determines whether theoretical value becomes real value.
          </p>

          {/* Predicted / Proven split legend */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[11px] font-semibold uppercase tracking-wider">
            <div className="flex items-center justify-center gap-2 rounded-md bg-slate-100 py-1.5 text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              Prediction
            </div>
            <span className="text-slate-400">|</span>
            <div className="flex items-center justify-center gap-2 rounded-md bg-emerald-50 py-1.5 text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Proven value
            </div>
          </div>

          {/* Waterfall Diagram */}
          <div className="overflow-x-auto pt-3 pb-2">
            <div className="flex items-start gap-1.5 min-w-[780px]">
              {[
                { label: 'LEI', desc: 'Learning\nImprovement', color: 'bg-slate-600', note: 'How much did learning improve?', side: 'pred' },
                { label: 'C × M', desc: 'Capability ×\nMotivation', color: 'bg-blue-600', note: 'What skills & motivation changed?', side: 'pred' },
                { label: 'PE Ideal', desc: 'Predicted\nCapacity', color: 'bg-purple-500', note: 'Theoretical workforce potential', side: 'pred' },
                { label: 'ROE', desc: 'Return on\nExpectation', color: 'bg-teal-600', note: 'Did it become behavior?', side: 'gate' },
                { label: 'PE Valid', desc: 'Proven\nCapacity', color: 'bg-purple-700', note: 'Actual workforce performance', side: 'proven' },
                { label: 'KPI', desc: 'Business\nMetrics', color: 'bg-emerald-600', note: 'Which metrics moved?', side: 'proven' },
                { label: 'ROI', desc: 'Financial\nReturn', color: 'bg-amber-600', note: 'Dollar value and return', side: 'proven' },
              ].map((step, i, arr) => {
                const isRoe = step.label === 'ROE';
                return (
                  <React.Fragment key={step.label}>
                    <div className={cn('flex flex-col items-center flex-1 min-w-0', isRoe && 'relative')}>
                      {isRoe && (
                        <span className="mb-2 text-[9px] font-bold uppercase tracking-[0.12em] text-teal-700">
                          Gate
                        </span>
                      )}
                      {!isRoe && <span className="mb-2 h-[14px]" aria-hidden="true" />}
                      <div
                        className={cn(
                          'w-full rounded-lg py-2.5 px-1 text-center text-white shadow-sm',
                          step.color,
                          isRoe &&
                            'ring-4 ring-teal-300 ring-offset-2 ring-offset-white shadow-lg shadow-teal-500/40 scale-y-110'
                        )}
                      >
                        <p className={cn('text-xs font-bold', isRoe && 'text-sm tracking-wide')}>{step.label}</p>
                      </div>
                      <p
                        className={cn(
                          'text-[10px] font-medium mt-2 whitespace-pre-line text-center leading-tight',
                          isRoe ? 'text-teal-800 font-semibold' : 'text-slate-500'
                        )}
                      >
                        {step.desc}
                      </p>
                      <p
                        className={cn(
                          'text-[10px] mt-0.5 italic text-center',
                          isRoe ? 'text-teal-600' : 'text-slate-400'
                        )}
                      >
                        {step.note}
                      </p>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex items-center pt-6 flex-shrink-0">
                        <ArrowRight
                          className={cn(
                            'h-4 w-4',
                            step.label === 'PE Ideal' || step.label === 'ROE'
                              ? 'text-teal-500'
                              : 'text-slate-300'
                          )}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-teal-100 bg-gradient-to-r from-teal-50 to-indigo-50 p-4 text-sm text-slate-700">
            <p className="flex items-start gap-2">
              <span className="mt-0.5 text-teal-600">
                <Activity className="h-4 w-4" />
              </span>
              <span>
                <strong className="text-teal-800">Key insight:</strong> Everything to the left of ROE is <em>prediction</em>. Everything to the right is <em>proven value</em>. <strong>ROE is the validation gate</strong> that converts predictions into evidence. Without it, all KPI and ROI projections are theoretical.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* The ROE Formula */}
      <Card id="roe-formula" className="border-2 border-teal-100 bg-gradient-to-br from-teal-50/30 to-white scroll-mt-24 transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-teal-600" />
            The ROE Formula
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-teal-200 rounded-lg p-4 text-center">
            <p className="text-xl font-mono font-bold text-teal-900">ROE = 0.4 × LPS + 0.6 × SI</p>
            <p className="text-sm text-slate-500 mt-2">
              Where: <span className="font-mono">LPS = Learner Performance Shift</span> and <span className="font-mono">SI = Stakeholder Impact</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg space-y-2">
              <p className="text-sm font-bold text-cyan-800">LPS — Learner Performance Shift (40% weight)</p>
              <div className="bg-white rounded p-2 font-mono text-xs text-cyan-900">LPS = 0.6 × Skills Uplift + 0.4 × Confidence to Apply</div>
              <p className="text-xs text-cyan-700">Captures the L2 (Kirkpatrick Level 2) evidence: did learners gain skills AND feel confident enough to apply them? Skills without confidence won't transfer.</p>
              <p className="text-xs text-cyan-600"><strong>Data sources:</strong> Pre/post assessment scores, certification rates, knowledge retention tests, post-training confidence surveys.</p>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
              <p className="text-sm font-bold text-emerald-800">SI — Stakeholder Impact (60% weight)</p>
              <div className="bg-white rounded p-2 font-mono text-xs text-emerald-900">SI = Weighted L3 Manager Observation Score</div>
              <p className="text-xs text-emerald-700">Captures the L3 (Kirkpatrick Level 3) evidence: did managers observe actual behavior change on the job? This is the definitive proof that learning transferred.</p>
              <p className="text-xs text-emerald-600"><strong>Data sources:</strong> Structured manager observations at 60-90 days: behavior change (35%), skill application (30%), performance improvement (20%), manager confidence (15%).</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Why L3 carries 60% weight:</strong> L2 (learning shift) proves people <em>learned</em>. L3 (behavior change) proves people <em>changed</em>. In practice, the gap between learning and doing is where most value is lost. L3 is weighted higher because on-the-job behavior change is the strongest predictor of KPI impact.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ROE's Impact on PE */}
      <Card id="pe-validated" className="scroll-mt-24 transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-purple-600" />
            How ROE Transforms PE
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-purple-200 rounded-lg p-4 text-center">
            <p className="text-xl font-mono font-bold text-purple-900">PE Validated = PE Ideal × ROE</p>
            <p className="text-sm text-slate-500 mt-2">The reality multiplier that converts theoretical capacity into proven capacity</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 border border-purple-100 rounded-lg text-center bg-purple-50/30">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">PE Ideal</p>
              <p className="text-sm font-bold text-purple-600 mt-0.5">C × M = Predicted</p>
              <p className="text-[10px] text-slate-500 mt-1">What your workforce <em>could</em> deliver if all training transferred perfectly</p>
            </div>
            <div className="p-3 border border-teal-200 rounded-lg text-center bg-teal-50">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">× ROE</p>
              <p className="text-sm font-bold text-teal-600 mt-0.5">Reality Multiplier</p>
              <p className="text-[10px] text-slate-500 mt-1">Percentage that actually became behavior change (from L2 + L3 data)</p>
            </div>
            <div className="p-3 border border-purple-200 rounded-lg text-center bg-purple-50">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">= PE Validated</p>
              <p className="text-sm font-bold text-purple-700 mt-0.5">Proven Capacity</p>
              <p className="text-[10px] text-slate-500 mt-1">What your workforce <em>actually</em> delivers — drives KPI and ROI</p>
            </div>
          </div>

          <p className="text-sm text-slate-600">
            <strong>Example:</strong> If PE Ideal is 55% (C at 77% × M at 71%) and ROE is 60%, then PE Validated = 55% × 60% = <strong>33%</strong>. The 22% gap is "capability leakage" — learning that was acquired but never applied on the job. This gap is the most actionable metric in your entire L&D measurement system.
          </p>

          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
            <strong>Transfer Gap = PE Ideal − PE Validated.</strong> A gap &gt;20% is critical and signals systemic failure in learning transfer. A gap &lt;5% indicates an exceptionally effective learning ecosystem.
          </div>
        </CardContent>
      </Card>

      {/* ROE's Impact on KPI and ROI */}
      <Card id="kpi-roi" className="scroll-mt-24 transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            ROE's Impact on KPI and ROI
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <p>
            PE Validated is the score that drives all downstream financial projections. When ROE is low, the entire value chain compresses:
          </p>
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-teal-600">1</span>
              </div>
              <p><strong>PE Validated drives KPI elasticity:</strong> Each business KPI (productivity, quality, safety, CSAT) has an elasticity coefficient linking it to PE. A lower PE Validated means less KPI movement per learning dollar invested.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-teal-600">2</span>
              </div>
              <p><strong>KPI improvement determines financial value:</strong> Each KPI has a baseline dollar value per employee. KPI improvement × baseline = financial impact. Lower PE Validated = lower KPI improvement = lower dollar value.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-teal-600">3</span>
              </div>
              <p><strong>ROI = (Financial Impact − Investment) / Investment:</strong> If ROE is 50% instead of 80%, your financial impact is 37.5% lower — turning a 200% ROI into a 87.5% ROI on the same investment. <em>ROE is the single largest variable in determining whether your learning investment delivers positive returns.</em></p>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800">
            <strong>Bottom line:</strong> ROE is the biggest lever in your learning ROI. Improving ROE from 50% to 70% (a realistic goal through better manager support and application design) can double your financial return without spending a single additional dollar on training.
          </div>
        </CardContent>
      </Card>

      {/* ROE Component Deep Dives */}
      <div id="deep-dives" className="space-y-3 scroll-mt-24">
        <h2 className="text-lg font-semibold text-slate-900">ROE Component Deep Dives</h2>

        <ExpandableSection title="L2 Capability Shift (Skills Uplift)" icon={Target} iconColor="text-blue-600" testId="roe-ref-l2-capability">
          <div className="space-y-3 text-sm text-slate-700">
            <p>The average percentage improvement in skill assessment scores from pre-training to post-training. This is the core L2 measure of whether training built capability.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">Skills Uplift = (Post-Score − Pre-Score) / Pre-Score × 100</div>
            <p><strong>Where to find it:</strong> LMS assessment reports — compare average pre-test scores vs post-test scores per program. Format: 0-100 scale.</p>
            <p><strong>What good looks like:</strong> 15-25% uplift is typical for well-designed programs. &gt;30% suggests high-impact training or significant knowledge gaps. &lt;10% may indicate the wrong audience or ineffective content.</p>
            <p className="text-slate-500"><strong>Impact on ROE:</strong> Skills uplift carries 60% weight in the LPS calculation (which itself is 40% of ROE). Improving skills uplift from 20% to 35% increases LPS by ~9 points.</p>
          </div>
        </ExpandableSection>

        <ExpandableSection title="Confidence to Apply" icon={CheckCircle2} iconColor="text-cyan-600" testId="roe-ref-confidence">
          <div className="space-y-3 text-sm text-slate-700">
            <p>Learners' self-reported confidence that they can apply new skills on the job. This is the critical bridge between knowing and doing.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">Post-training survey: "How confident are you that you can apply what you learned?" (1-5 Likert × 20)</div>
            <p><strong>Why it matters:</strong> Even highly skilled employees won't change behavior if they lack confidence. Confidence is the #1 hidden barrier to ROE. It directly reduces the Learner Performance Shift and suppresses the entire C × M → ROE chain.</p>
            <p><strong>How to improve:</strong> Add practice scenarios, peer mentoring, safe-to-fail application opportunities, and manager coaching during the 30-90 day post-training window.</p>
          </div>
        </ExpandableSection>

        <ExpandableSection title="L3 Behavior Transfer (Manager Observations)" icon={Users} iconColor="text-emerald-600" testId="roe-ref-l3-behavior">
          <div className="space-y-3 text-sm text-slate-700">
            <p>The composite score from structured manager observations measuring whether learning actually changed on-the-job behavior. This carries 60% of the ROE weight.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">L3 = (0.35 × Behavior Change) + (0.30 × Skill Application) + (0.20 × Performance Improvement) + (0.15 × Manager Confidence)</div>
            <p><strong>Data collection:</strong> Structured observation forms administered at 60-90 days post-training. Managers rate each dimension on a 1-5 scale, converted to percentage.</p>
            <p><strong>What good looks like:</strong> L3 &gt;70% indicates strong behavior transfer. 50-70% is acceptable but indicates improvement opportunity. &lt;50% signals a transfer crisis — learning is being acquired but not applied.</p>
            <p className="text-amber-700 bg-amber-50 p-2 rounded"><strong>Critical insight:</strong> Manager support is the #1 predictor of L3 scores. Organizations that implement structured manager coaching see L3 improvements of 25-40% within one cycle. This is the most impactful, lowest-cost intervention available.</p>
          </div>
        </ExpandableSection>

        <ExpandableSection title="Motivation & Manager Support" icon={Zap} iconColor="text-amber-600" testId="roe-ref-motivation">
          <div className="space-y-3 text-sm text-slate-700">
            <p>While Motivation (M) is a separate PE component, it also directly influences ROE through the confidence and manager support channels.</p>
            <p><strong>The connection:</strong> High intrinsic motivation increases willingness to try new behaviors (boosting L3). High manager support increases both confidence to apply (boosting LPS) and observed behavior change (boosting SI). This creates a reinforcing cycle:</p>
            <div className="flex items-center justify-center gap-2 flex-wrap py-3">
              {['Manager Support', 'Confidence', 'Application', 'L3 Score', 'ROE'].map((s, i, a) => (
                <React.Fragment key={s}>
                  <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium">{s}</span>
                  {i < a.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
                </React.Fragment>
              ))}
            </div>
            <p className="text-slate-500"><strong>Implication:</strong> Improving manager support improves BOTH the M component of PE and the SI component of ROE — a double impact on PE Validated.</p>
          </div>
        </ExpandableSection>
      </div>

      {/* Using ROE in Practice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-slate-700" />
            Using ROE in Practice
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-slate-200 rounded-lg space-y-2">
              <p className="font-bold text-teal-700">ROE Health (Actuals)</p>
              <p className="text-xs text-slate-600">Import real L2 assessment data, motivation surveys, and L3 manager observations. The system calculates your actual ROE and identifies deficiencies — showing where in the transfer chain value is being lost.</p>
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => navigate('/roe-health')}>View ROE Health</Button>
            </div>
            <div className="p-4 border border-slate-200 rounded-lg space-y-2">
              <p className="font-bold text-teal-700">ROE Modeller (Scenarios)</p>
              <p className="text-xs text-slate-600">Model "what if" scenarios by adjusting L2, motivation, and L3 inputs. See how improving specific components (e.g., raising manager support from 50% to 70%) would impact ROE, PE Validated, KPI movement, and financial ROI.</p>
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => navigate('/roe-modeller')}>Open ROE Modeller</Button>
            </div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg space-y-2">
            <p className="font-bold text-teal-700">ROE Variance (Modelled vs Actual)</p>
            <p className="text-xs text-slate-600">Compare your modelled projections against actual ROE data to see where reality diverges from your assumptions. The variance for each component (Capability, Motivation, L3, PE, ROE) shows exactly where to focus improvement efforts.</p>
          </div>
        </CardContent>
      </Card>

      {/* Cross-References */}
      <RelatedReferences
        activeRef="roe"
        extras={[
          { href: '/roe-health', label: 'ROE Health' },
          { href: '/roe-modeller', label: 'ROE Modeller' },
        ]}
      />
    </div>
    </PageShell>
  );
}
